import React, { useEffect, useRef, useState } from "react";
import { useCloud } from "../../hooks/useCloud";
import { toast } from "react-toastify";
import { Search, X, Plus, MoreVertical, User, Loader2 } from "lucide-react";
import store from "../../store/index";

const Staff = () => {
  const { addNewStaff, updateStaff, removeStaff } = useCloud();
  
  // State management
  const [loading, setLoading] = useState(true);
  const [staff, setStaff] = useState([]);
  const [filteredStaff, setFilteredStaff] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  
  // Form state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [contact, setContact] = useState("");
  const [role, setRole] = useState("Staff");
  const [displayValue, setDisplayValue] = useState("");
  const [realPassword, setRealPassword] = useState("");
  const [editingStaffId, setEditingStaffId] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  
  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);

  // Refs
  const menuRefs = useRef({});
  const modalRef = useRef(null);

  // Initial data fetch
  useEffect(() => {
    const fetchStaff = async () => {
      const staffData = store.staff.map((staff) => ({
        id: staff.id,
        name: staff.name,
        contact: staff.contact,
        email: staff.email || "",
        status: staff.status,
        role: staff.role,
        createdAt: staff.createdAt,
        updatedAt: staff.updatedAt,
      }));
      setStaff(staffData);
      setFilteredStaff(staffData);
      setLoading(false);
    };
    fetchStaff();
  }, []);

  // Search functionality
  useEffect(() => {
    if (searchQuery.trim()) {
      const filtered = staff.filter((member) =>
        member.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        member.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (member.contact && member.contact.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (member.email && member.email.toLowerCase().includes(searchQuery.toLowerCase())) ||
        member.role.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredStaff(filtered);
    } else {
      setFilteredStaff(staff);
    }
  }, [searchQuery, staff]);

  // Add click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Close menu if clicking outside
      if (openMenuId && !menuRefs.current[openMenuId]?.contains(event.target)) {
        setOpenMenuId(null);
      }

      // Close modal if clicking outside
      if (isModalOpen && modalRef.current && !modalRef.current.contains(event.target)) {
        handleCancel();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openMenuId, isModalOpen]);

  // Add handleEdit function
  const handleEdit = (staffMember) => {
    setName(staffMember.name);
    setEmail(staffMember.email || "");
    setContact(staffMember.contact || "");
    setRole(staffMember.role || "Staff");
    setEditingStaffId(staffMember.id);
    setIsModalOpen(true);
    setOpenMenuId(null);
    setFormErrors({});
  };

  // Form validation
  const validateForm = () => {
    const errors = {};
    
    if (!name.trim()) {
      errors.name = "Name is required";
    }

    if (!contact.trim()) {
      errors.contact = "Contact is required";
    } else if (!/^\d{10}$/.test(contact.trim())) {
      errors.contact = "Contact must be a 10-digit number";
    }

    if (!editingStaffId && !realPassword) {
      errors.password = "Password is required";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Form handlers
  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      if (editingStaffId) {
        const payload = {
          staffId: editingStaffId,
          name,
          email,
          role,
          contact,
          status: "Active",
        };

        const response = await updateStaff(payload);
        
        if (response.status === "success") {
          const updatedStaff = {
            id: editingStaffId,
            name,
            email,
            contact,
            role,
            status: "Active",
            updatedAt: new Date().toISOString(),
          };

          setStaff((prevStaff) =>
            prevStaff.map((staff) =>
              staff.id === editingStaffId
                ? { ...staff, ...updatedStaff }
                : staff
            )
          );
          setFilteredStaff((prevFiltered) =>
            prevFiltered.map((staff) =>
              staff.id === editingStaffId
                ? { ...staff, ...updatedStaff }
                : staff
            )
          );
          toast.success(response.message || "Staff updated successfully");
          handleCancel();
        } else {
          toast.error(response.message || "Failed to update staff");
        }
      } else {
        const payload = {
          name,
          email,
          password: realPassword,
          role,
          contact,
        };

        const response = await addNewStaff(payload);
        
        if (response.status === "success") {
          const newStaff = {
            id: response.data.staffId,
            name,
            email,
            contact,
            role,
            status: "Active",
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          };
          
          setStaff((prevStaff) => [...prevStaff, newStaff]);
          setFilteredStaff((prevFiltered) => [...prevFiltered, newStaff]);
          toast.success(response.message || "Staff added successfully");
          handleCancel();
        } else {
          toast.error(response.message || "Failed to add staff");
        }
      }
    } catch (error) {
      console.error("Operation failed:", error);
      toast.error("An unexpected error occurred");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (staffId) => {
    setIsDeleting(staffId);
    try {
      const response = await removeStaff(staffId);

      if (response.status === "success") {
        setStaff((prevStaff) =>
          prevStaff.filter((staff) => staff.id !== staffId)
        );
        setFilteredStaff((prevFiltered) =>
          prevFiltered.filter((staff) => staff.id !== staffId)
        );
        setOpenMenuId(null);
        toast.success(response.message || "Staff removed successfully");
      } else {
        toast.error(response.message || "Failed to remove staff");
      }
    } catch (error) {
      console.error("Delete operation failed:", error);
      toast.error("An unexpected error occurred while removing staff");
    } finally {
      setIsDeleting(null);
    }
  };

  const handleCancel = () => {
    setName("");
    setEmail("");
    setContact("");
    setRole("Staff");
    setDisplayValue("");
    setRealPassword("");
    setIsModalOpen(false);
    setEditingStaffId(null);
    setFormErrors({});
  };

  const handleAddNew = () => {
    setEditingStaffId(null);
    setName("");
    setEmail("");
    setContact("");
    setRole("Staff");
    setDisplayValue("");
    setRealPassword("");
    setIsModalOpen(true);
    setFormErrors({});
  };

  // Render functions
  const renderStaffCard = (staff) => {
    return (
      <div key={staff.id} className="staff-card">
        <div className="staff-card-content">
          <div className="staff-card-header">
            <div className="staff-card-profile">
              <div className="staff-card-avatar">
                <User className="staff-card-avatar-icon" />
              </div>
              <div className="staff-card-info">
                <h3 className="staff-card-name">{staff.name}</h3>
              </div>
            </div>
            <div className="staff-card-menu" ref={(el) => (menuRefs.current[staff.id] = el)}>
              <button
                type="button"
                onClick={() => setOpenMenuId(openMenuId === staff.id ? null : staff.id)}
                className="staff-card-menu-button"
              >
                <MoreVertical size={16} />
              </button>

              {openMenuId === staff.id && (
                <div className="staff-card-menu-dropdown">
                  <div className="py-1">
                    <button
                      onClick={() => handleEdit(staff)}
                      className="staff-card-menu-item"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(staff.id)}
                      disabled={isDeleting === staff.id}
                      className="staff-card-menu-item staff-card-menu-item--delete"
                    >
                      {isDeleting === staff.id && <Loader2 size={14} className="animate-spin" />}
                      Delete
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="staff-card-grid">
            <div className="staff-card-field">
              <p className="staff-card-label">Contact</p>
              <p className="staff-card-value">
                {staff.contact || "Not available"}
              </p>
            </div>
            <div className="staff-card-field">
              <p className="staff-card-label">Email</p>
              <p className="staff-card-value staff-card-value--truncate" title={staff.email}>
                {staff.email || "Not available"}
              </p>
            </div>
          </div>

          <div className="staff-card-footer">
            <div className="staff-card-footer-content">
              <p className="staff-card-timestamp">
                Added on{" "}
                {staff.createdAt
                  ? new Date(staff.createdAt).toLocaleDateString()
                  : "Invalid Date"}
              </p>
            </div>
          </div>
        </div>
        <span
          className={`staff-card-role ${
            staff.role === "Manager"
              ? "staff-card-role--manager"
              : "staff-card-role--staff"
          }`}
        >
          {staff.role || "Staff"}
        </span>
      </div>
    );
  };

  const renderEmptyState = () => (
    <div className="staff-empty">
      <User className="staff-empty-icon" />
      <h3 className="staff-empty-title">No staff added yet</h3>
      <p className="staff-empty-text">Get started by adding your first staff.</p>
      <button onClick={handleAddNew} className="staff-empty-button">
        Add Staff
      </button>
    </div>
  );

  const loadingUserAnimation = () => (
    <div className="staff-loading-card">
      <div className="staff-loading-content">
        <div className="staff-loading-header">
          <div className="staff-loading-profile">
            <div className="staff-loading-avatar" />
            <div className="staff-loading-info">
              <div className="staff-loading-name" />
              <div className="staff-loading-id" />
            </div>
          </div>
          <div className="staff-loading-menu" />
        </div>

        <div className="staff-loading-grid">
          <div className="staff-loading-field">
            <div className="staff-loading-label" />
            <div className="staff-loading-value" />
          </div>
          <div className="staff-loading-field">
            <div className="staff-loading-label" />
            <div className="staff-loading-value" />
          </div>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="staff-page">
        <div className="staff-loading-grid">
          {[1, 2, 3].map((user) => loadingUserAnimation())}
        </div>
      </div>
    );
  }

  return (
    <div className="staff-page">
      <div className="staff-header">
        <div className="staff-search">
          <div className="staff-search-icon">
            <Search size={16} />
          </div>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="staff-search-input"
            placeholder="Search staff..."
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery("")} className="staff-search-clear">
              <X size={14} />
            </button>
          )}
        </div>
        <button onClick={handleAddNew} className="staff-add-button">
          <Plus size={16} />
          Add New Staff
        </button>
      </div>

      {filteredStaff.length > 0 ? (
        <div className="staff-grid">
          {filteredStaff.map(renderStaffCard)}
        </div>
      ) : (
        renderEmptyState()
      )}

      {isModalOpen && (
        <div className="staff-modal-overlay">
          <div ref={modalRef} className="staff-modal">
            <div className="staff-modal-header">
              <h3 className="staff-modal-title">
                {editingStaffId ? "Edit Staff" : "Add New Staff"}
              </h3>
              <button onClick={handleCancel} className="staff-modal-close">
                <X size={20} />
              </button>
            </div>

            <div className="staff-modal-body">
              <div className="staff-form-grid">
                <div className="staff-form-field">
                  <label className="staff-form-label">Name*</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => {
                      setName(e.target.value);
                      setFormErrors({ ...formErrors, name: "" });
                    }}
                    placeholder="Enter name"
                    className={`staff-form-input ${formErrors.name ? 'staff-form-input--error' : ''}`}
                  />
                  {formErrors.name && (
                    <p className="staff-form-error">{formErrors.name}</p>
                  )}
                </div>

                <div className="staff-form-field">
                  <label className="staff-form-label">Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter email"
                    className="staff-form-input"
                  />
                </div>

                <div className="staff-form-field">
                  <label className="staff-form-label">Contact*</label>
                  <input
                    type="text"
                    value={contact}
                    onChange={(e) => {
                      setContact(e.target.value);
                      setFormErrors({ ...formErrors, contact: "" });
                    }}
                    placeholder="Enter contact number"
                    className={`staff-form-input ${formErrors.contact ? 'staff-form-input--error' : ''}`}
                  />
                  {formErrors.contact && (
                    <p className="staff-form-error">{formErrors.contact}</p>
                  )}
                </div>

                <div className="staff-form-field">
                  <label className="staff-form-label">Role</label>
                  <select
                    value={role}
                    onChange={(e) => setRole(e.target.value)}
                    className="staff-form-select"
                  >
                    <option value="Staff">Staff</option>
                    <option value="Manager">Manager</option>
                  </select>
                </div>

                {!editingStaffId && (
                  <div className="staff-form-field col-span-2">
                    <label className="staff-form-label">Password*</label>
                    <input
                      type="text"
                      value={displayValue}
                      onChange={(e) => {
                        const input = e.target;
                        const newValue = input.value;
                        
                        if (newValue.length > realPassword.length) {
                          const addedChar = newValue[newValue.length - 1];
                          setRealPassword((prev) => prev + addedChar);
                          setDisplayValue("•".repeat(newValue.length));
                        } else {
                          setRealPassword((prev) =>
                            prev.slice(0, newValue.length)
                          );
                          setDisplayValue("•".repeat(newValue.length));
                        }
                        setFormErrors({ ...formErrors, password: "" });
                      }}
                      placeholder="Enter password"
                      autoComplete="off"
                      spellCheck="false"
                      autoCorrect="off"
                      className={`staff-form-input ${formErrors.password ? 'staff-form-input--error' : ''}`}
                      style={{
                        fontFamily: "password",
                        WebkitTextSecurity: "disc",
                      }}
                      required
                    />
                    {formErrors.password && (
                      <p className="staff-form-error">{formErrors.password}</p>
                    )}
                  </div>
                )}
              </div>

              <div className="staff-modal-footer">
                <button
                  onClick={handleCancel}
                  disabled={isSubmitting}
                  className="staff-button staff-button--cancel"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={!name || !contact || (!editingStaffId && !realPassword) || isSubmitting}
                  className={`staff-button staff-button--submit ${
                    !name || !contact || (!editingStaffId && !realPassword) || isSubmitting
                      ? ""
                      : "staff-button--submit-active"
                  }`}
                >
                  {isSubmitting && <Loader2 size={16} className="animate-spin" />}
                  {editingStaffId ? "Update Staff" : "Add Staff"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Staff;
